<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="() => {}" :static="true" class="relative z-50">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      </TransitionChild>

      <!-- Dialog -->
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-[var(--input-background)] p-6 text-left align-middle shadow-xl transition-all">
              <div class="flex flex-col items-center text-center">
                <!-- Error Icon (customizable) -->
                <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full" :class="iconContainerClass || 'bg-red-100 dark:bg-red-900/30'">
                  <slot name="icon">
                    <svg
                      class="h-10 w-10"
                      :class="iconClass || 'text-red-500 dark:text-red-400'"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                  </slot>
                </div>

                <!-- Title -->
                <DialogTitle
                  as="h3"
                  class="text-lg font-semibold leading-6 mt-4 text-[var(--text-color)] dark:text-white"
                >
                  {{ title }}
                </DialogTitle>

                <!-- Description -->
                <div class="mt-2">
                  <p class="text-md text-[var(--text-secondary)] dark:text-gray-400" v-html="message">
                  </p>
                </div>

                <!-- Countdown Progress Bar (shows only when redirect is enabled) -->
                <div v-if="redirectUrl && autoCloseDelay > 0 && remainingTime > 0" class="w-full mt-6">
                  <div class="h-1 w-full bg-[var(--surface-border)] dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-red-500 transition-all duration-200 ease-linear"
                      :style="{ width: `${progressPercentage}%` }"
                    />
                  </div>
                  <p class="text-xs text-[var(--text-secondary)] dark:text-gray-400 mt-2">
                    {{ countDownMessage }} {{ Math.ceil(remainingTime / 1000) }}s...
                  </p>
                </div>

                <!-- Action Buttons -->
                <div v-if="showButtons" class="mt-6 flex gap-3">
                  <button
                    v-if="showPrimaryButton"
                    type="button"
                    class="inline-flex justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
                    :class="primaryButtonClass || 'bg-red-500 hover:bg-red-600'"
                    @click="onPrimaryAction"
                  >
                    {{ primaryButtonText }}
                  </button>
                  <button
                    v-if="showSecondaryButton"
                    type="button"
                    class="inline-flex justify-center rounded-lg px-4 py-2 text-sm font-semibold text-[var(--reverse-text-color)] shadow-sm transition-colors"
                    :class="secondaryButtonClass || 'bg-[var(--text-color)]'"
                    @click="onSecondaryAction"
                  >
                    {{ secondaryButtonText }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { useRouter } from 'vue-router'
import { watch, ref, onUnmounted } from 'vue'

const router = useRouter()

interface Props {
  isOpen: boolean
  title?: string
  message?: string
  details?: string
  showButtons?: boolean
  showPrimaryButton?: boolean
  showSecondaryButton?: boolean
  primaryButtonText?: string
  secondaryButtonText?: string
  primaryButtonClass?: string
  secondaryButtonClass?: string
  iconClass?: string
  iconContainerClass?: string
  redirectUrl?: string
  autoCloseDelay?: number
  preserveScroll?: boolean
  countDownMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  title: 'Error',
  message: 'An error occurred.',
  details: '',
  showButtons: true,
  showPrimaryButton: true,
  showSecondaryButton: true,
  primaryButtonText: 'Try Again',
  secondaryButtonText: 'Close',
  primaryButtonClass: '',
  secondaryButtonClass: '',
  iconClass: '',
  iconContainerClass: '',
  redirectUrl: '',
  autoCloseDelay: 0,
  preserveScroll: false,
  countDownMessage: 'Closing in'
})

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'primaryAction'): void
  (e: 'secondaryAction'): void
  (e: 'close'): void
}>()

const isDetailsExpanded = ref(false)
const remainingTime = ref(0)
const progressPercentage = ref(100)
let progressInterval: number | null = null
let closedManually = false

// Handle auto-close and redirect with progress
watch(() => props.isOpen, (newValue) => {
  if (newValue && props.autoCloseDelay > 0) {
    closedManually = false;
    // Initialize countdown
    remainingTime.value = props.autoCloseDelay
    progressPercentage.value = 100

    // Clear any existing interval
    if (progressInterval) {
      clearInterval(progressInterval)
    }

    // Update progress every 10ms
    const updateFrequency = 10
    const totalSteps = props.autoCloseDelay / updateFrequency
    const progressStep = 100 / totalSteps

    progressInterval = window.setInterval(() => {
      remainingTime.value = Math.max(0, remainingTime.value - updateFrequency)
      progressPercentage.value = (remainingTime.value / props.autoCloseDelay) * 100

      if (remainingTime.value <= 0) {
        if (progressInterval) {
          clearInterval(progressInterval)
        }
      }
    }, updateFrequency)

    // Set timeout for actual close
    setTimeout(() => {
      if (!closedManually) onClose()
    }, props.autoCloseDelay)
  } else {
    // Clear interval when dialog is closed
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }
  }
})

// Cleanup on component unmount
onUnmounted(() => {
  if (progressInterval) {
    clearInterval(progressInterval)
  }
})

const onClose = () => {
  closedManually = true;
  emit('update:isOpen', false)
  emit('close')

  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
  
  if (props.redirectUrl) {
    if (props.preserveScroll) {
      router.push(props.redirectUrl)
    } else {
      window.location.href = props.redirectUrl
    }
  }
}

const onPrimaryAction = () => {
  emit('primaryAction')
  if (!props.redirectUrl) {
    onClose()
  }
}

const onSecondaryAction = () => {
  emit('secondaryAction')
  onClose()
}
</script> 